import React, { useEffect, useState } from 'react';

const Hero = ({ scrollToSection }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    if (prefersReducedMotion) return;

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section id="home" className="hero min-h-screen bg-gradient-to-br from-primary-dark via-primary-green-dark to-primary-green flex items-center justify-center text-center relative overflow-hidden" aria-labelledby="home-heading">
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute w-96 h-96 bg-primary-green-light/20 rounded-full blur-3xl"
          style={{
            left: `${20 + mousePosition.x * 0.02}%`,
            top: `${20 + mousePosition.y * 0.02}%`,
            transition: 'all 0.3s ease-out'
          }}
        ></div>
        <div
          className="absolute w-96 h-96 bg-accent-gold/20 rounded-full blur-3xl"
          style={{
            right: `${20 - mousePosition.x * 0.02}%`,
            bottom: `${20 - mousePosition.y * 0.02}%`,
            transition: 'all 0.3s ease-out'
          }}
        ></div>
      </div>

      <div className="absolute inset-0 opacity-10" aria-hidden="true" style={{
        backgroundImage: `linear-gradient(rgba(176, 228, 204, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(176, 228, 204, 0.08) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }}></div>

      <div className="hero-content max-w-6xl px-[5%] animate-fadeInUp relative z-10">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-primary-green-light/30 mb-6">
          <span className="w-2 h-2 bg-accent-gold rounded-full animate-ping" aria-hidden="true"></span>
          <span className="text-xs md:text-sm text-primary-green-light font-medium">Trusted by Industry Leaders</span>
        </div>

        <h1 id="home-heading" className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4 md:mb-6" aria-label="Jay Ambe Engineering, precision CNC parts manufacturer and precision VMC parts manufacturer for custom machined parts as per customer requirements">
          <span className="block bg-gradient-to-r from-white via-primary-green-light to-accent-gold bg-clip-text text-transparent animate-pulse">
            Your Trusted Partner
          </span>
          <br />
          <span className="block bg-gradient-to-r from-accent-gold via-[#F4D03F] to-accent-orange bg-clip-text text-transparent" style={{
            backgroundSize: '200% auto',
            animation: 'gradientShift 4s ease infinite'
          }}>
            High Precision Manufacturing
          </span>
        </h1>

        <div className="space-y-2 mb-8 md:mb-12">
          <p className="text-base sm:text-lg md:text-xl opacity-90 font-light text-white animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <span className="font-semibold text-primary-green-light">8 Years of Excellence</span> | <span className="font-semibold text-primary-green-light">30 Years Engineering Experience</span>
          </p>
          <p className="text-sm sm:text-base md:text-lg opacity-80 font-light animate-fadeInUp text-white" style={{ animationDelay: '0.4s' }}>
            Specializing in VMC CNC Job Work & Precision Components
          </p>
          <p className="sr-only">
            Jay Ambe Engineering provides precision machining, CNC work, VMC work, best customized parts for CNC work and best customized parts for VMC work in Makarpura GIDC, Vadodara.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" style={{ animationDelay: '0.6s' }}>
          <a
            href="#contact"
            onClick={(e) => scrollToSection(e, '#contact')}
            className="cta-button group inline-flex items-center gap-3 px-8 md:px-12 py-4 md:py-5 bg-gradient-to-r from-accent-gold to-accent-orange text-primary-dark font-semibold text-base md:text-lg rounded-full shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-gold relative overflow-hidden"
          >
            <span className="shine"></span>
            <span className="relative z-10">Get Quote Now</span>
            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>

          <a
            href="/jay-ambe-engineering-brochure.pdf"
            download="jay-ambe-engineering-brochure.pdf"
            aria-label="Download Jay Ambe Engineering precision CNC and VMC work brochure"
            className="group inline-flex items-center gap-2 px-8 py-4 rounded-full border border-primary-green-light/40 text-primary-green-light font-medium bg-white/10 hover:bg-primary-green-light/10 hover:border-primary-green-light transition-all duration-300 backdrop-blur-sm"
          >
            <span>Download Brochure</span>
            <svg className="w-5 h-5 group-hover:translate-y-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0 0l4-4m-4 4l-4-4M4 21h16" />
            </svg>
          </a>
        </div>

        <div className="flex flex-wrap justify-center gap-8 mt-12 md:mt-16 opacity-80">
          <div className="flex items-center gap-2 text-white/70">
            <svg className="w-5 h-5 text-accent-gold" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
            </svg>
            <span className="text-sm">100% Quality</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <svg className="w-5 h-5 text-accent-gold" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path>
            </svg>
            <span className="text-sm">On-Time Delivery</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
